export type BrandProjectAction = "edit" | "delete";

export type BrandProjectActionContext = {
  isDefault: boolean;
  isProjectOwner: boolean;
  isWorkspaceOwner: boolean;
  isReadOnly: boolean;
};

export type ExistingWorkspaceIntegrationAssignmentContext = {
  isProjectOwner: boolean;
  isWorkspaceOwner: boolean;
  isReadOnly: boolean;
};

export type BusinessGroupManagementContext = {
  isProjectOwner: boolean;
  isWorkspaceOwner: boolean;
  isReadOnly: boolean;
};

export type BusinessGroupMemberManagementContext = BusinessGroupManagementContext & {
  isDefault: boolean;
  scope?: 'workspace' | 'private';
};

export type BusinessGroupOwnerRecord = {
  id?: string;
  scope?: 'workspace' | 'private';
  visibility?: 'WORKSPACE' | 'PRIVATE';
  primaryOwnerUserId?: string | null;
  createdByUserId?: string | null;
};

export type BusinessGroupCreatorUser = {
  id: string;
  name: string;
};

export type ConnectionAssignmentCeiling = "DISABLED" | "WRITE";

export type ConnectionBusinessGroupAssignmentContext = {
  isConnectionOwner: boolean;
  isWorkspaceOwner: boolean;
};

type ConnectionOwnershipRecord = {
  connectedByUserId?: string | null;
  /** Server-authoritative capability for the current viewer. */
  canManageAccess?: boolean;
};

/** Returns the recorded creator's display name, including legacy owner-only groups. */
export const getBusinessGroupCreatorName = (
  businessGroup: BusinessGroupOwnerRecord,
  workspaceUsers: BusinessGroupCreatorUser[],
): string | undefined => {
  const creatorUserId = businessGroup.createdByUserId || businessGroup.primaryOwnerUserId;
  return workspaceUsers.find((user) => user.id === creatorUserId)?.name;
};

export const canManageBusinessGroup = ({
  isProjectOwner,
  isWorkspaceOwner,
  isReadOnly,
}: BusinessGroupManagementContext): boolean =>
  !isReadOnly && (isWorkspaceOwner || isProjectOwner);

/** Private Business groups are creator-only and cannot invite Workspace users. */
export const canManageBusinessGroupMembers = ({
  isDefault,
  scope,
  isProjectOwner,
  isWorkspaceOwner,
  isReadOnly,
}: BusinessGroupMemberManagementContext): boolean =>
  !isDefault &&
  scope !== 'private' &&
  canManageBusinessGroup({ isProjectOwner, isWorkspaceOwner, isReadOnly });

const normalizeIdentity = (value?: string | null) => value?.trim().toLowerCase() || "";

/**
 * Member grants are owned by the Business-group creator. The primary owner is
 * only a compatibility fallback for legacy groups that do not record createdBy.
 */
export const isBusinessGroupCreator = (
  businessGroup: BusinessGroupOwnerRecord,
  currentUserIds: string[],
): boolean => {
  const creatorIdentity = normalizeIdentity(
    businessGroup.createdByUserId || businessGroup.primaryOwnerUserId,
  );
  if (!creatorIdentity) return false;

  return currentUserIds
    .map(normalizeIdentity)
    .filter(Boolean)
    .includes(creatorIdentity);
};

export const getManageableBusinessGroups = <T extends BusinessGroupOwnerRecord>(
  businessGroups: T[],
  currentUserIds: string[],
  isWorkspaceOwner: boolean,
): T[] => {
  if (isWorkspaceOwner) return businessGroups;
  const identities = new Set(currentUserIds.map(normalizeIdentity).filter(Boolean));
  return businessGroups.filter((businessGroup) => {
    const managementIdentities = [
      businessGroup.primaryOwnerUserId,
      businessGroup.createdByUserId,
    ]
      .map(normalizeIdentity)
      .filter(Boolean);

    return managementIdentities.some((identity) => identities.has(identity));
  });
};

/**
 * The application owner controls the positive delegation ceiling and may
 * attach their own application to any active public Business group. A private
 * group remains an owner boundary: an application owner may only target a
 * private group they also own. This does not grant Business-group management;
 * the target list is a dedicated assignment-only surface and the backend
 * still validates connection owner and Workspace membership.
 */
export const getApplicationOwnerAssignableBusinessGroups = <
  T extends BusinessGroupOwnerRecord,
>(
  businessGroups: T[],
  manageableBusinessGroups: T[],
  isCurrentApplicationOwner: boolean,
): T[] => {
  if (!isCurrentApplicationOwner) return manageableBusinessGroups;
  const manageablePrivateGroupIds = new Set(
    manageableBusinessGroups
      .filter((businessGroup) => {
        const visibility = businessGroup.visibility ||
          (businessGroup.scope === 'private' ? 'PRIVATE' : 'WORKSPACE');
        return visibility === 'PRIVATE';
      })
      .map((businessGroup) => businessGroup.id)
      .filter((id): id is string => Boolean(id)),
  );
  return businessGroups.filter((businessGroup) => {
    const visibility = businessGroup.visibility ||
      (businessGroup.scope === 'private' ? 'PRIVATE' : 'WORKSPACE');
    return visibility !== 'PRIVATE' || manageablePrivateGroupIds.has(businessGroup.id || "");
  });
};

/**
 * Moving a connected application into a Business group changes its workspace
 * boundary. Legacy connection-management grants only control account access;
 * they must not expose this assignment flow. The connection owner and the
 * Workspace Owner are the two actors accepted by the assignment API.
 */
export const canAssignConnectionToBusinessGroups = ({
  isConnectionOwner,
  isWorkspaceOwner,
}: ConnectionBusinessGroupAssignmentContext): boolean =>
  isConnectionOwner || isWorkspaceOwner;

/**
 * A connection may start with a positive delegation ceiling only when the
 * server confirms that the current viewer can manage its access. The recorded
 * connection owner remains a legacy fallback while older contract responses
 * do not include that capability. This controls member access inside an
 * existing Business-group assignment; it does not grant authority to change
 * which Business group owns the connection.
 */
export const getConnectionAssignmentCeiling = (
  connection: ConnectionOwnershipRecord,
  currentUserId?: string | null,
): ConnectionAssignmentCeiling =>
  connection.canManageAccess ||
  (normalizeIdentity(connection.connectedByUserId) &&
    normalizeIdentity(connection.connectedByUserId) === normalizeIdentity(currentUserId))
    ? "WRITE"
    : "DISABLED";

/**
 * Assigning an existing Workspace integration changes the Business-group
 * boundary, so only its current owner or a Workspace owner may do it.
 */
export const canAddExistingWorkspaceIntegrations = ({
  isProjectOwner,
  isWorkspaceOwner,
  isReadOnly,
}: ExistingWorkspaceIntegrationAssignmentContext): boolean =>
  canManageBusinessGroup({ isProjectOwner, isWorkspaceOwner, isReadOnly });

/**
 * Business-group actions are intentionally owner-scoped. The default group is
 * required for every Workspace, so it may be edited but can never be deleted.
 */
export const getBrandProjectActions = ({
  isDefault,
  isProjectOwner,
  isWorkspaceOwner,
  isReadOnly,
}: BrandProjectActionContext): BrandProjectAction[] => {
  if (!canManageBusinessGroup({ isProjectOwner, isWorkspaceOwner, isReadOnly })) return [];
  return isDefault ? ["edit"] : ["edit", "delete"];
};
